import { useTranslation } from 'react-i18next';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { ROWS_PER_PAGE } from '../utils/paginationUtils';
import IconButton from '@mui/material/IconButton';
import TablePagination from '@mui/material/TablePagination';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import BarChartIcon from '@mui/icons-material/BarChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NoData from './NoData';

export default function EventsTable({ setSelectedEvent, onCopy, events, showStats = true, pagination }) {
  const { t } = useTranslation();
  const { totalItems, page, limit, onPageChange, onRowsPerPageChange } = pagination;

  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();

  const rowClickHandler = (id) => {
    return () => {
      setSelectedEvent(id);
    };
  };

  return events.length ? (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ '& > th': { whiteSpace: 'nowrap' } }}>
              <TableCell>{t('ID')}</TableCell>
              {user.role !== 'organizer' && <TableCell>{t('Organizer')}</TableCell>}
              <TableCell>{t('Date/Time')}</TableCell>
              <TableCell>{t('Name')}</TableCell>
              <TableCell>{t('Location')}</TableCell>
              <TableCell>{t('City')}</TableCell>
              <TableCell align="center">{t('Status')}</TableCell>
              {showStats && (
                <>
                  <TableCell align="right">{t('Sold')}</TableCell>
                  <TableCell align="right">{dayjs().add(-1, 'day').format('DD/MM')}</TableCell>
                  <TableCell align="right">{dayjs().format('DD/MM')}</TableCell>
                </>
              )}
              <TableCell align="right">{t('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(
              ({
                id,
                date,
                has_event_date,
                title,
                location,
                city,
                status,
                is_one_plus_one_free,
                is_three_plus_one_free,
                total_sales,
                today_sales,
                yesterday_sales,
                organizer,
                min_rec_age,
              }) => (
                <TableRow
                  {...(user.role === 'cashier' ? { onClick: rowClickHandler(id) } : {})}
                  {...(user.role !== 'cashier' ? { component: Link, to: `${id}` } : {})}
                  key={id}
                  sx={{
                    cursor: 'pointer',
                    '& > td': { whiteSpace: 'nowrap' },
                    textDecoration: 'none',
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': (theme) => ({
                      backgroundColor: theme.palette.action.hover,
                    }),
                  }}
                >
                  <TableCell>{id}</TableCell>
                  {user.role !== 'organizer' && <TableCell>{organizer}</TableCell>}
                  <TableCell>
                    {has_event_date === 0
                      ? ['organizer', 'cashier'].includes(user.role)
                        ? '-'
                        : ''
                      : dayjs(date).format('dddd DD MMMM YYYY HH:mm ')}
                  </TableCell>
                  <TableCell>
                    {title}
                    {min_rec_age && (
                      <Chip sx={{ ml: 1, borderRadius: 1 }} size="small" label={`${min_rec_age}+`} variant="filled" />
                    )}
                    {!!is_one_plus_one_free && (
                      <Chip
                        sx={{ ml: 1, borderRadius: 1 }}
                        size="small"
                        label={t('1+1 Free')}
                        variant="outlined"
                        color="success"
                      />
                    )}
                    {!!is_three_plus_one_free && (
                      <Chip
                        sx={{ ml: 1, borderRadius: 1 }}
                        size="small"
                        label={t('3+1 Free')}
                        variant="outlined"
                        color="success"
                      />
                    )}
                  </TableCell>
                  <TableCell>{location}</TableCell>
                  <TableCell>{city}</TableCell>
                  <TableCell align="center">
                    {status === 'SELLING' ? (
                      <Chip
                        sx={{ mt: 1, borderRadius: 1 }}
                        size="small"
                        label={t(`${status.replace('_', ' ')}`)}
                        variant="outlined"
                        color="success"
                      />
                    ) : status === 'NOT_SELLING' ? (
                      <Chip
                        sx={{ mt: 1, borderRadius: 1 }}
                        size="small"
                        label={t(`${status.replace('_', ' ')}`)}
                        variant="outlined"
                        color="error"
                      />
                    ) : (
                      <Chip
                        sx={{ mt: 1, borderRadius: 1 }}
                        size="small"
                        label={t(`${status.replace('_', ' ')}`)}
                        variant="outlined"
                        color="warning"
                      />
                    )}
                  </TableCell>
                  {showStats && (
                    <>
                      <TableCell align="right">{total_sales}</TableCell>
                      <TableCell align="right">{yesterday_sales}</TableCell>
                      <TableCell align="right">{today_sales}</TableCell>
                    </>
                  )}
                  {user.role === 'cashier' && (
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          navigate(`${id}`);
                        }}
                        size="medium"
                      >
                        <BarChartIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                  )}
                  {user.role === 'organizer' && (
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          onCopy(id);
                        }}
                        size="medium"
                      >
                        <ContentCopyIcon fontSize="medium" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalItems}
        page={page - 1}
        onPageChange={onPageChange}
        rowsPerPage={limit}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={ROWS_PER_PAGE}
        labelRowsPerPage={t('rowsPaginator')}
      />
    </Paper>
  ) : (
    <NoData />
  );
}
